import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Ban, CheckCheck, Copy, Download, Eye, FileDown, PackageCheck, Pencil, Send } from 'lucide-react'
import {
  Alert, Card, Confirm, Loading, Modal, StatusPill, useToast,
} from '@/components/ui'
import { AttachmentList, AuditTrail } from '@/components/docs/shared'
import { DocumentActions } from '@/components/docs/DocumentActions'
import { ApprovalTrail } from '@/components/docs/ApprovalTrail'
import { DeleteDocument } from '@/components/docs/DeleteDocument'
import { DocumentTrail } from '@/components/docs/DocumentTrail'
import { PANE_WIDTH, PdfPreviewPane, useSplitWidth, type PdfPreview } from '@/components/docs/PdfPreviewPane'
import { useAuth } from '@/lib/auth'
import { fetchLines, fetchOne, useMaterials, usePeople, useSites, useVendors } from '@/lib/data'
import { callFunction, supabase } from '@/lib/supabase'
import { CAN, can, chainStep, factorShort, isEditable, needsConversion } from '@/lib/domain'
import { amountInWords, ddmmyyyy, qty, rupees } from '@/lib/format'
import { enqueue } from '@/lib/sync'
import type { Material, PoLine, PurchaseOrder } from '@/lib/types'

export function PoDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()

  const [po, setPo] = useState<PurchaseOrder | null>(null)
  const [lines, setLines] = useState<PoLine[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  // CMD, 02-11-2026: the draft preview opens in a pane beside the page, as on
  // the work order.
  const [preview, setPreview] = useState<PdfPreview | null>(null)
  const split = useSplitWidth(preview !== null)
  const closePreview = useCallback(() => setPreview(null), [])
  const [confirm, setConfirm] = useState<'send' | 'ack' | 'void' | 'shortclose' | null>(null)
  const [grnCount, setGrnCount] = useState<number | null>(null)
  // 0225: copying this order into a new draft, as the work order's Duplicate.
  const [copying, setCopying] = useState(false)
  const [copyBusy, setCopyBusy] = useState(false)

  const { data: materials } = useMaterials()
  const { data: vendors } = useVendors()
  const { data: sites } = useSites()
  // Names on screen, not email addresses. canCorrect below still COMPARES on
  // po.creator_email, which is correct and must stay: identity is the email,
  // the name is only how it is drawn.
  const people = usePeople()

  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const header = await fetchOne<PurchaseOrder>('purchase_orders', id)
      if (!header) throw new Error(`Purchase order ${id} was not found, or you do not have access to it.`)
      setPo(header)
      setLines(await fetchLines<PoLine>('po_line_items', 'po_id', id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the purchase order')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  // How many receipts exist against this order. The database refuses a
  // force-close once there are any; this is only so the screen can explain
  // why the button is not there rather than letting the user find out by
  // pressing it.
  useEffect(() => {
    let live = true
    supabase.from('grns').select('id', { count: 'exact', head: true })
      .eq('po_id', id).neq('status', 'REJECTED')
      .then(({ count }) => { if (live) setGrnCount(count ?? 0) })
    return () => { live = false }
  }, [id])

  if (loading) return <Loading label="Loading purchase order…" />
  if (error || !po) return <Alert tone="error">{error}</Alert>

  const role = profile!.role
  const vendor = vendors.find((v) => v.id === po.vendor_id)
  const shipTo = sites.find((s) => s.id === po.site_id)
  const canDespatch = po.status === 'APPROVED' && can(role, 'despatchPo')
  const canCorrect = isEditable(po.status) && (po.creator_email === profile!.email || role === 'admin')
  const canAck = po.status === 'VENDIN' && can(role, 'despatchPo')
  const pdfReady = ['APPROVED', 'VENDIN', 'VENDACK', 'PARTIALLY_RECEIVED', 'CLOSED'].includes(po.status)

  // Is this order living under the Board's five-stage chain, or the two-stage
  // workflow that was in force when it was raised?
  const chained = ((po as any).chain_version ?? 1) >= 2

  // Declaring a live order null and void, and only while the vendor has
  // delivered nothing at all. Board 11-08-2026 moved this to the Head of
  // Procurement and both Procurement Managers.
  const voidable = ['APPROVED', 'VENDIN', 'VENDACK'].includes(po.status)
  const canForceClose = voidable && grnCount === 0 && can(role, 'closePo')

  // Different thing entirely from voiding: material HAS arrived, just not all
  // of it, and the vendor is not sending the rest. SAP calls this the delivery
  // completed indicator. What came in stays received and stays payable.
  const canShortClose = po.status === 'PARTIALLY_RECEIVED' && can(role, 'closePo')

  async function downloadPdf() {
    setPdfBusy(true)
    try {
      const res = await callFunction('generate-po-pdf', { po_id: po!.id, download: true })
      if (!res.ok) throw new Error(await res.text())
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${po!.id}.pdf`
      a.click()
      setTimeout(() => URL.revokeObjectURL(a.href), 4000)
      toast.success('Purchase order PDF downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not generate the PDF')
    } finally {
      setPdfBusy(false)
    }
  }

  /**
   * CMD, 02-11-2026: the order as it will print, before it is approved -
   * unsigned, and "not a purchase order" across every page (generate-po-pdf
   * sees to both). Nothing is stored and the order is not touched.
   */
  async function previewPdf() {
    setPdfBusy(true)
    try {
      const res = await callFunction('generate-po-pdf', { po_id: po!.id, preview: true })
      if (!res.ok) {
        const text = (await res.text()).trim()
        let message = text
        try { const p = JSON.parse(text); if (typeof p?.error === 'string') message = p.error } catch { /* as sent */ }
        throw new Error(message || 'Could not prepare the preview')
      }
      setPreview({
        blob: await res.blob(),
        fileName: `${po!.id}-DRAFT-PREVIEW.pdf`,
        title: `Draft preview - ${po!.id}`,
        note: 'Draft for review - not a purchase order, not for issue',
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not prepare the preview')
    } finally {
      setPdfBusy(false)
    }
  }

  /**
   * Anurupa, 03-11-2026: a repeat order to the same vendor for the same site,
   * without retyping it. The database copies it into a new DRAFT prepared by
   * this user - lines, terms, quotation and papers, but not the requisition
   * link - then he goes straight into the builder to correct it. The original
   * is not touched.
   */
  async function copyPo() {
    setCopyBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_po_duplicate', { p_po_id: po!.id })
      if (error) throw new Error(error.message)
      setCopying(false)
      toast.success(`Copied into a new draft, ${data}. Check it, then submit it for review.`)
      navigate(`/pos/${data}/edit`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not copy the purchase order')
    } finally {
      setCopyBusy(false)
    }
  }

  async function openStoredPdf() {
    if (!po?.po_pdf_path) return
    const { data, error } = await supabase.storage.from('po-pdfs').createSignedUrl(po.po_pdf_path, 3600)
    if (error || !data) { toast.error('Stored PDF is not available yet — generate it first.'); return }
    window.open(data.signedUrl, '_blank')
  }

  return (
    // On a large screen the page gives up the pane's width while a preview
    // is open, so the order and its PDF sit side by side.
    <div className="space-y-4 transition-[margin] duration-300 ease-out motion-reduce:transition-none"
         style={{ marginRight: split ? PANE_WIDTH : undefined }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/pos" className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All purchase orders</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{po.id}</h1>
          <StatusPill status={po.status} />
          {po.is_legacy && <span className="pill-grey">migrated</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canCorrect && (
            <Link to={`/pos/${po.id}/edit`} className="btn-primary btn-sm">
              <Pencil className="h-4 w-4" /> {po.status === 'RETURNED' ? 'Correct and resubmit' : 'Edit'}
            </Link>
          )}
          {pdfReady && (
            <>
              <button className="btn-secondary btn-sm" onClick={downloadPdf} disabled={pdfBusy}>
                <FileDown className="h-4 w-4" /> {pdfBusy ? 'Generating…' : 'Download PDF'}
              </button>
              {po.po_pdf_path && (
                <button className="btn-ghost btn-sm" onClick={openStoredPdf}>
                  <Download className="h-4 w-4" /> Stored copy
                </button>
              )}
            </>
          )}
          {/* CMD, 02-11-2026: the same document before approval, unsigned and
              watermarked, for anybody who can see the order. */}
          {['DRAFT', 'SUBMITTED', 'REVIEWED', 'RETURNED'].includes(po.status) && (
            <button className="btn-secondary btn-sm" onClick={() => void previewPdf()} disabled={pdfBusy}
                    title="Opens the printable order beside this page - unsigned and marked DRAFT, not for issue">
              <Eye className="h-4 w-4" /> {pdfBusy ? 'Preparing…' : 'Preview PDF'}
            </button>
          )}
          {can(role, 'createPo') && (
            <button className="btn-secondary btn-sm" onClick={() => setCopying(true)} disabled={copyBusy}
                    title="A new draft to the same vendor for the same site, with everything filled in from this order">
              <Copy className="h-4 w-4" /> Copy to new PO
            </button>
          )}
          {canDespatch && (
            <button className="btn-primary btn-sm" onClick={() => setConfirm('send')}>
              <Send className="h-4 w-4" /> Send to vendor
            </button>
          )}
          {canAck && (
            <button className="btn-success btn-sm" onClick={() => setConfirm('ack')}>
              <CheckCheck className="h-4 w-4" /> Vendor acknowledged
            </button>
          )}
          <DeleteDocument docType="PO" docId={po.id} status={po.status}
                          creatorEmail={po.creator_email} backTo="/pos" />
          {canForceClose && (
            <button className="btn-danger btn-sm" onClick={() => setConfirm('void')}>
              <Ban className="h-4 w-4" /> Declare null and void
            </button>
          )}
          {canShortClose && (
            <button className="btn-warning btn-sm" onClick={() => setConfirm('shortclose')}>
              <PackageCheck className="h-4 w-4" /> Close — no more expected
            </button>
          )}
        </div>
      </div>

      {po.copied_from && (
        <p className="-mt-2 text-xs text-slate-500">
          Copied from{' '}
          <Link to={`/pos/${po.copied_from}`} className="font-medium text-oval-700 hover:underline">
            {po.copied_from}
          </Link>
        </p>
      )}

      <Modal open={copying} onClose={() => setCopying(false)}
             title={`Copy ${po.id} to a new purchase order`}
             subtitle={`A new purchase order, in DRAFT, prepared by you - to ${vendor?.company_name ?? 'the same vendor'} for ${shipTo?.site_name ?? 'the same site'}. This order stays exactly as it is.`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setCopying(false)}>Cancel</button>
                 <button className="btn-primary" disabled={copyBusy} onClick={() => void copyPo()}>
                   <Copy className="h-4 w-4" /> {copyBusy ? 'Copying…' : 'Copy it'}
                 </button>
               </>
             }>
        <div className="space-y-2 text-sm text-slate-700">
          <p>
            <strong>Copied:</strong> the vendor, ship-to and bill-to sites, every material with its
            quantity, unit, rate and GST, the payment, delivery and warranty terms, special
            instructions, validity, the ticked standard terms, the vendor&rsquo;s quotation, and the
            attached papers.
          </p>
          <p>
            <strong>Not copied:</strong> the number, status, approvals, history, PDF, receipts and
            payments &mdash; they belong to this order. The <strong>requisition link</strong> is not
            carried either, so the requisition is not counted twice.
          </p>
          <p>
            <strong>Left blank for you:</strong> a delivery date that has passed, a quotation date
            too old for the rules, and the JV Original Indent No. A material no longer active in
            the master is left out.
          </p>
          <p className="text-xs text-slate-500">
            You go straight to the new draft to check it. It then goes through the full review
            and approval chain as a new order.
          </p>
        </div>
      </Modal>

      {po.status === 'REJECTED' && (
        <Alert tone="error" title="This purchase order was rejected and is permanently stopped">
          {po.rejection_reason ?? 'No reason was recorded.'} A fresh order has to be raised.
        </Alert>
      )}
      {po.status === 'CLOSED' && (po as any).short_close_reason && (
        <Alert tone="warning" title="Closed with quantity undelivered">
          {(po as any).short_close_reason}
          {' '}What arrived is still payable. The shortfall was released back to the
          requisition.
        </Alert>
      )}
      {po.status === 'FORCED_CLOSE' && (
        <Alert tone="error" title="This purchase order has been declared null and void">
          {(po as any).force_close_reason ?? 'No reason was recorded.'}
          {' '}Nothing can be received against it. The quantities it was holding on its requisition
          have been released, so a replacement order can be raised.
        </Alert>
      )}
      {voidable && grnCount !== null && grnCount > 0
        && ['procurement_head', 'admin'].includes(role) && (
        <Alert tone="info" title="This order can no longer be voided">
          {grnCount} goods receipt{grnCount === 1 ? ' has' : 's have'} already been raised against it.
          Material has been delivered and the vendor is owed for it, so the order has to run its
          normal course and close once he is settled.
        </Alert>
      )}
      {po.status === 'RETURNED' && (
        <Alert tone="warning" title="Returned for revision">{po.return_reason ?? 'No note was recorded.'}</Alert>
      )}
      {po.status === 'APPROVED' && (
        <Alert tone="success" title="Approved by the Procurement Manager">
          The PDF has been generated. Press <strong>Send to vendor</strong> once you have emailed or messaged it
          across — the order then moves to <strong>Sent to vendor</strong>. Record the vendor's confirmation
          afterwards so the site can receive against it.
        </Alert>
      )}
      {po.status === 'VENDIN' && (
        <Alert tone="info" title="Waiting for the vendor to confirm">
          The site store-man cannot raise a goods receipt until you record the vendor's acknowledgement.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Order details" className="lg:col-span-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <D label="Vendor" value={vendor?.company_name ?? po.vendor_id} />
            <D label="Vendor GSTIN" value={vendor?.is_gst_registered ? (vendor.gstin ?? '—') : 'Unregistered'} />
            <D label="Vendor state" value={vendor?.state_name ?? '—'} />
            <D label="Ship to" value={shipTo?.site_name ?? po.site_id} />
            <D label="Bill to" value={po.billing_site_id} />
            <D label="Against requisition" value={po.pr_id ?? 'None — urgent purchase'} />
            <D label="Raised on" value={ddmmyyyy(po.date_created)} />
            <D label="Delivery by" value={ddmmyyyy(po.delivery_date)} />
            <D label="Quotation ref" value={`${po.vendor_quotation_ref ?? '—'} (${ddmmyyyy(po.vendor_quotation_date)})`} />
            {/* JV-financed sites only. Shown when the order actually carries
                one, so an ordinary order — and every order raised before this
                rule existed — reads exactly as it did before. */}
            {po.original_indent_no && (
              <D label="Original indent"
                 value={`${po.original_indent_no} (${ddmmyyyy(po.original_indent_date)})`} />
            )}
            {/* Names, not email addresses — management 01-09-2026. The columns
                still hold the email; only what is drawn has changed. */}
            <D label="Prepared by" value={po.creator_email ? people.name({ email: po.creator_email }) : '—'} />
            <D label="Reviewed by" value={po.reviewer_email ? people.name({ email: po.reviewer_email }) : '—'} />
            <D label="Approved by" value={po.approver_email ? people.name({ email: po.approver_email }) : '—'} />
            <D label="Payment terms" value={po.payment_terms ?? '—'} />
            <D label="Delivery terms" value={po.delivery_terms ?? '—'} />
            <D label="Warranty" value={po.warranty ?? '—'} />
            {/* Orders raised before 05-09-2026 were issued without a stated
                validity, and show a dash rather than a figure invented now. */}
            <D label="PO validity"
               value={po.po_validity_days
                 ? `${po.po_validity_days} ${Number(po.po_validity_days) === 1 ? 'day' : 'days'} from issue`
                 : '—'} />
          </dl>
          {po.special_instructions && (
            <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <p className="text-xs font-semibold text-slate-500">Special instructions</p>
              <p className="text-slate-700">{po.special_instructions}</p>
            </div>
          )}
          {/* Amber, and labelled, so that anyone reading this order alongside
              the vendor's copy can see at a glance which part the vendor never
              received. */}
          {po.internal_vendor_note && (
            <div className="mt-3 rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50/60 px-3 py-2 text-sm">
              <p className="flex items-center gap-2 text-xs font-semibold text-amber-800">
                Why this vendor — internal record
                <span className="pill-amber">Not printed on the PO</span>
              </p>
              <p className="mt-1 whitespace-pre-wrap text-slate-700">{po.internal_vendor_note}</p>
            </div>
          )}
        </Card>

        <Card
          title="Decision"
          subtitle={chained
            ? (po.status === 'SUBMITTED'
                ? `Stage ${(po as any).current_step ?? 1} of 5 — ${chainStep('PO', (po as any).current_step ?? 1)?.label ?? ''}`
                : 'No decision pending')
            : po.status === 'SUBMITTED' ? 'Four-eyes review by the Head of Stores'
              : po.status === 'REVIEWED' ? 'Final approval by the Procurement Manager' : 'No decision pending'}
        >
          <DocumentActions docType="PO" docId={po.id} status={po.status}
                           chainVersion={(po as any).chain_version}
                           currentStep={(po as any).current_step}
                           creatorId={(po as any).prepared_by}
                           onDone={load} className="flex flex-col gap-2" />
          {!['SUBMITTED', 'REVIEWED'].includes(po.status) && (
            <p className="text-sm text-slate-500">
              {po.status === 'DRAFT'
                ? 'Still a draft — the Procurement Executive has to submit it.'
                : 'This order has already passed the approval chain.'}
            </p>
          )}
          <div className="mt-3">
            <ApprovalTrail docType="PO" docId={po.id} status={po.status}
                           chainVersion={(po as any).chain_version}
                           currentStep={(po as any).current_step} />
          </div>
        </Card>
      </div>

      <Card title="Line items">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>SL</th><th>Material</th><th>HSN</th>
                <th className="num">Ordered (stock)</th><th className="num">Ordered (purchase)</th>
                <th className="num">Rate</th><th className="num">GST %</th><th className="num">Amount</th>
                <th className="num">Received</th><th className="num">Pending</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const m = byId[l.material_id]
                return (
                  <tr key={l.id}>
                    <td className="text-xs text-slate-400">{l.sl_no}</td>
                    <td>
                      <span className="block text-sm font-medium">{m?.item_name ?? l.material_id}</span>
                      {l.item_specifications && <span className="block text-xs text-slate-500">{l.item_specifications}</span>}
                    </td>
                    <td className="font-mono text-xs">{m?.hsn_code ?? '—'}</td>
                    <td className="num">{qty(l.quantity_ordered)} <span className="text-xs text-slate-400">{l.stock_uom}</span></td>
                    <td className="num">
                      {qty(l.quantity_in_purchase_uom)} <span className="text-xs text-slate-400">{l.purchase_uom}</span>
                      {needsConversion(m) && (
                        <span className="block text-[11px] text-oval-700">×{factorShort(l.conversion_factor)}</span>
                      )}
                    </td>
                    <td className="num text-xs">{rupees(l.unit_rate)}</td>
                    <td className="num text-xs">{po.gst_treatment === 'NONE' ? 'NA' : l.gst_rate_percent}</td>
                    <td className="num text-xs font-semibold">{rupees(l.line_amount)}</td>
                    <td className="num text-xs text-emerald-700">{qty(l.quantity_received_so_far)}</td>
                    <td className="num text-xs font-medium text-amber-700">{qty(l.quantity_remaining)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex justify-end">
          <div className="w-full max-w-md space-y-1.5 rounded-lg border border-slate-200 p-4">
            <Row label="Subtotal" value={rupees(po.subtotal)} />
            {po.gst_treatment === 'NONE' && <Row label="GST" value="Not applicable (unregistered vendor)" muted />}
            {po.gst_treatment === 'CGST_SGST' && (
              <>
                <Row label="CGST" value={rupees(po.cgst_amount)} />
                <Row label="SGST" value={rupees(po.sgst_amount)} />
              </>
            )}
            {po.gst_treatment === 'IGST' && <Row label="IGST" value={rupees(po.igst_amount)} />}
            <div className="border-t border-slate-200 pt-2"><Row label="TOTAL" value={rupees(po.total_amount)} strong /></div>
            <p className="pt-1 text-xs italic text-slate-600">
              {po.total_in_words || amountInWords(Number(po.total_amount))}
            </p>
          </div>
        </div>
      </Card>

      {role && CAN.viewDocumentTrail.includes(role) && <DocumentTrail poId={po.id} />}

      <AttachmentList docType="PO" docId={po.id}
                      subtitle="Vendor quotation, correspondence and anything else filed against this order" />

      <AuditTrail docType="PO" docId={po.id} />

      <Confirm
        open={confirm === 'send'}
        title={`Send ${po.id} to the vendor?`}
        message={<>
          The status moves to <strong>Sent to vendor</strong> and the PDF is downloaded for you.
          Email or message it to {vendor?.company_name} yourself — the system does not send it.
        </>}
        confirmLabel="Yes, mark as sent"
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          await enqueue({ kind: 'po.despatch', docType: 'PO', localDocId: po.id, label: `Send ${po.id} to vendor`, payload: { id: po.id } })
          await downloadPdf()
          setConfirm(null); toast.success('Marked as sent to the vendor.'); void load()
        }}
      />

      <Confirm
        open={confirm === 'ack'}
        title={`Record the vendor's acknowledgement for ${po.id}?`}
        message={<>Once acknowledged, the store-man at {shipTo?.site_name ?? po.site_id} can raise a goods receipt against this order.</>}
        confirmLabel="Yes, vendor has confirmed"
        tone="success"
        requireReason
        reasonLabel="How was it acknowledged? (email, WhatsApp, phone…)"
        onCancel={() => setConfirm(null)}
        onConfirm={async (remarks) => {
          await enqueue({ kind: 'po.ack', docType: 'PO', localDocId: po.id, label: `Vendor ack ${po.id}`, payload: { id: po.id, remarks } })
          setConfirm(null); toast.success('Vendor acknowledgement recorded.'); void load()
        }}
      />

      <Confirm
        open={confirm === 'shortclose'}
        title={`Close ${po.id} with quantity still outstanding?`}
        message={<>
          Everything already delivered stays delivered and stays payable — this does
          <strong> not</strong> cancel anything.
          <br /><br />
          It records that {vendor?.company_name} will send no more, so the order stops
          appearing as awaiting delivery. The undelivered quantity goes back onto
          {po.pr_id ? ` ${po.pr_id}` : ' the requisition'}, so the site can buy it elsewhere
          if it still needs it.
          <br /><br />
          <strong>This cannot be undone.</strong>
        </>}
        confirmLabel="Yes, close it"
        tone="warning"
        requireReason
        reasonLabel="Why is no more expected? (kept permanently)"
        onCancel={() => setConfirm(null)}
        onConfirm={async (reason) => {
          try {
            const { error } = await supabase.rpc('rpc_po_short_close', {
              p_po_id: po.id, p_reason: reason,
            })
            if (error) throw new Error(error.message)
            setConfirm(null)
            toast.success(`${po.id} closed. Nothing further is expected against it.`)
            void load()
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not close the order')
          }
        }}
      />

      <Confirm
        open={confirm === 'void'}
        title={`Declare ${po.id} null and void?`}
        message={<>
          This order is cancelled outright. {vendor?.company_name} must be told separately —
          the system does not write to him.
          <br /><br />
          The quantities this order was holding on {po.pr_id ?? 'its requisition'} are released,
          so a replacement order can be raised straight away. The rate on it stops counting as a
          last purchase rate, because nobody ever bought at it.
          <br /><br />
          <strong>This cannot be undone.</strong>
        </>}
        confirmLabel="Yes, declare it void"
        tone="danger"
        requireReason
        reasonLabel="Why is this order being cancelled? (kept permanently)"
        onCancel={() => setConfirm(null)}
        onConfirm={async (reason) => {
          try {
            const { error } = await supabase.rpc('rpc_po_force_close', {
              p_po_id: po.id, p_reason: reason,
            })
            if (error) throw new Error(error.message)
            setConfirm(null)
            toast.success(`${po.id} is now null and void.`)
            void load()
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not void the order')
          }
        }}
      />

      <PdfPreviewPane preview={preview} onClose={closePreview} />
    </div>
  )
}

const D = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-slate-800">{value}</dd>
  </div>
)

const Row = ({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className={strong ? 'text-sm font-bold text-slate-800' : 'text-sm text-slate-600'}>{label}</span>
    <span className={strong ? 'text-base font-bold tabular-nums text-slate-900'
      : muted ? 'text-xs italic text-slate-500' : 'text-sm tabular-nums text-slate-800'}>{value}</span>
  </div>
)
